import { Durations } from '@ptml/design-system';
import { css, keyframes } from 'styled-components';
import { LAYOUT_EVENTS } from './constants';

export const cssOpacityFade = ({
    show,
    enterDuration = Durations.long500,
    exitDuration = Durations.short150,
}: {
    show: boolean;
    enterDuration?: number;
    exitDuration?: number;
}) => css`
    opacity: ${show ? 1 : 0};
    pointer-events: ${show ? 'auto' : 'none'};
    transition: opacity ${show ? enterDuration : exitDuration}ms linear;
`;

export const blurAndGrayscale = keyframes`
    0% {
        filter: grayscale(0) blur(0);
    }
    100% {
        filter: grayscale(0.8) blur(3px);
    }
`;

export const slideDownIn = keyframes`
    from {
        opacity: 0;
        transform: translate3d(0, -10%, 0);
    }
    to {
        opacity: 1;
        transform: translate3d(0, 0%, 0);
    }
`;

export const slideUpIn = keyframes`
    from {
        opacity: 0;
        transform: translate3d(0, 25%, 0) scale3d(.92, .92, 1);
    }
    to {
        opacity: 1;
        transform: translate3d(0, 0%, 0)  scale3d(1, 1, 1);
    }
`;

export const slideRightIn = keyframes`
    from {
        opacity: 0;
        transform: translate3d(-10%, 0, 0);
    }
    to {
        opacity: 1;
        transform: translate3d(0%, 0, 0);
    }
`;

export const slideLeftIn = keyframes`
    from {
        opacity: 0;
        transform: translate3d(10%, 0, 0);
    }
    to {
        opacity: 1;
        transform: translate3d(0%, 0, 0);
    }
`;

export const fadeIn = keyframes`
  0% { opacity: 0;  }
    100% { opacity: 1;  }
`;

export const fadeInAnimation = css`
    animation-name: ${fadeIn};
`;

export const scaleIn = keyframes`
    0% { opacity: 0; transform: scale3d(0.8,0.8,1); }
    100% { opacity: 1; transform: scale3d(1,1,1); }
`;

/**
 * Pulse animation using GREEN45 color at 0.2 opacity
 */
export const pulseGreen45_02 = keyframes`
    0% {
        box-shadow: 0 0 0 0 rgba(9, 134, 73, 0.2);
    }
    70% {
        box-shadow: 0 0 0 4px rgba(9, 134, 73, 0);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(9, 134, 73, 0);
    }
`;

/**
 * Pulse animation using GREEN45 color at 0.8 opacity
 */
export const pulseGreen45_08 = keyframes`
    0% {
        box-shadow: 0 0 0 0 rgba(9, 134, 73, 0.8);
    }
    70% {
        box-shadow: 0 0 0 4px rgba(9, 134, 73, 0);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(9, 134, 73, 0);
    }
`;

/**
 * Pulse animation using RED25 color at 0.8 opacity
 */
export const pulseRed25_08 = keyframes`


    0% {
        box-shadow: 0 0 0 0 rgba(255, 190, 190, 0.8);
    }
    70% {
        box-shadow: 0 0 0 6px rgba(255, 190, 190, 0);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(255, 190, 190, 0);
    }
`;

export const moveGradient = keyframes`
    0% {
    background-position: 0 0;
  }
    100% {
    background-position: 100% 0;
  }
`;

export const CollapseSidebar = () =>
    document.dispatchEvent(new CustomEvent(LAYOUT_EVENTS.COLLAPSE_SIDEBAR));
export const ExpandSidebar = () =>
    document.dispatchEvent(new CustomEvent(LAYOUT_EVENTS.EXPAND_SIDEBAR));

export const isNumeric = (string_: string) => {
    if (typeof string_ !== 'string') {
        return false;
    } // we only process strings!
    return (
        !Number.isNaN(Number(string_)) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
        !Number.isNaN(Number.parseFloat(string_))
    ); // ...and ensure strings of whitespace fail
};

export const primitiveToBoolean = (
    value: string | number | boolean | null | undefined,
): boolean => {
    if (typeof value === 'string') {
        return value.toLowerCase() === 'true' || !!+value; // here we parse to number first
    }

    return !!value;
};

/**
 * Generates a unique ID by combining the provided ID with a random string.
 * Useful for creating unique notification IDs or other temporary identifiers.
 *
 * @param id - The base ID to make unique
 * @returns A string in the format `{id}_{randomString}`
 */
export const getUniqueId = (id: string | number): string => {
    const stringRadix = 36;
    return `${id}_${Math.random().toString(stringRadix).slice(2, 11)}`;
};

/**
 * Generates a unique ID with timestamp prefix for scenarios requiring
 * time-based uniqueness.
 *
 * @returns A string in the format `{timestamp}_{randomString}`
 */
export const getTimestampUniqueId = (): string => {
    const stringRadix = 36;
    return `${Date.now()}_${Math.random().toString(stringRadix).slice(2, 11)}`;
};
